import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Fade, alpha, useTheme } from '@mui/material'

import { PAGE_BANNERS } from '../config/pageBanners'

const BANNER_WIDTH = 520
const MIN_WIDTH = 260   // narrower than this the art and title are cropped too much: hide it
const BANNER_MS = 7000  // how long each banner stays (same as the navbar strip)

// Banners of the current page, for the middle of the page header row (between <Header /> and the
// page's actions). They take turns with the same crossfade as the navbar strip; hovering pauses.
// It takes the free space in the row, shows the banner centered and cropped on the right when the
// space is short, and hides itself on narrow screens or when there is no room.
const PageBanner = () => {
  const theme = useTheme()
  const { pathname } = useLocation()
  const boxRef = useRef(null)
  const [fits, setFits] = useState(true)
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const page = pathname.split('/')[1]
  const banners = useMemo(() => PAGE_BANNERS[page] || [], [page])

  // a different page starts again from its first banner
  useEffect(() => {
    setIndex(0)
  }, [page])

  // load them once so the fade-in isn't blank
  useEffect(() => {
    banners.forEach(({ src }) => { const img = new Image(); img.src = src })
  }, [banners])

  useEffect(() => {
    if (banners.length < 2 || paused || !fits) return
    const t = setTimeout(() => setIndex((i) => (i + 1) % banners.length), BANNER_MS)
    return () => clearTimeout(t)
  }, [index, banners.length, paused, fits])

  useEffect(() => {
    const el = boxRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => setFits(entry.contentRect.width >= MIN_WIDTH))
    ro.observe(el)
    return () => ro.disconnect()
  }, [banners.length])

  if (banners.length === 0) return null

  const banner = banners[index % banners.length]
  const accent = theme.palette.secondary.main
  const isDark = theme.palette.mode === 'dark'

  return (
    <Box
      ref={boxRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        display: { xs: 'none', lg: 'flex' },   // same 1200px breakpoint the pages use for the wide layout
        justifyContent: 'center',
      }}
    >
      {fits && (
        <Fade in key={banner.src} timeout={600}>
          <Box
            component="img"
            src={banner.src}
            alt={banner.alt}
            title={banner.alt}
            draggable={false}
            sx={{
              display: 'block',
              height: 44,
              width: BANNER_WIDTH,
              maxWidth: '100%',
              objectFit: 'cover',
              objectPosition: 'left center',
              borderRadius: '10px',
              border: `1px solid ${alpha(accent, 0.25)}`,
              boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.15)',
              userSelect: 'none',
            }}
          />
        </Fade>
      )}
    </Box>
  )
}

export default PageBanner
